import DeleteIcon from '@mui/icons-material/Delete';

import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import type React from 'react';
import { type Column, StandardTable } from '../common/UI';

import type { Draft, LoadingStates } from './types';
import { formatUploadedDate, getLoadingKey } from './utils';

interface ReviewDraftsSectionProps {
  tenderId: string;
  drafts: Draft[];
  loadingStates: LoadingStates;
  onDeleteDraft: (draft: Draft) => void;
}

const ReviewDraftsSection: React.FC<ReviewDraftsSectionProps> = ({
  tenderId,
  drafts,
  loadingStates,
  onDeleteDraft,
}) => {
  const columns: Column<Draft>[] = [
    {
      id: 'name',
      label: 'Name',
      render: (draft) => {
        const fileName = draft.originalFilename || draft.filename || draft.name || 'Unknown';
        return (
          <Box>
            <Tooltip title={fileName}>
              <Typography
                variant="body2"
                sx={{ wordBreak: 'break-word', fontWeight: 500, fontSize: '1rem' }}
              >
                {fileName}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {formatUploadedDate(draft.uploadedAt)}
            </Typography>
            {(draft.conversionStatus === 'processing' ||
              draft.conversionStatus === 'failed' ||
              draft.conversionStatus === 'missing_file') && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {draft.conversionStatus === 'processing' && (
                  <CircularProgress size={10} sx={{ mr: 1 }} />
                )}
                <Typography
                  variant="caption"
                  color={
                    draft.conversionStatus === 'failed' || draft.conversionStatus === 'missing_file'
                      ? 'error'
                      : 'text.secondary'
                  }
                >
                  {draft.conversionStatus === 'missing_file'
                    ? 'Delete and reupload (File Missing)'
                    : draft.conversionStatus === 'failed'
                      ? 'Conversion Failed'
                      : 'Processing...'}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (draft) => {
        const fileName = draft.originalFilename || draft.filename || draft.name || 'Unknown';
        return (
          <IconButton
            edge="end"
            aria-label="delete"
            onClick={() => onDeleteDraft(draft)}
            disabled={!!loadingStates[getLoadingKey(tenderId, 'delete_draft', fileName)]}
            size="small"
          >
            {loadingStates[getLoadingKey(tenderId, 'delete_draft', fileName)] ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteIcon sx={{ fontSize: 28 }} />
            )}
          </IconButton>
        );
      },
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Drafts List */}
      <Box sx={{ mt: 2, maxHeight: '300px', overflowY: 'auto' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
          Uploaded Drafts
        </Typography>
        <StandardTable
          columns={columns}
          data={drafts}
          keyExtractor={(draft) =>
            draft.originalFilename || draft.filename || draft.name || 'Unknown'
          }
          size="small"
          emptyStateMessage="No drafts uploaded yet."
        />
      </Box>
    </Box>
  );
};

export default ReviewDraftsSection;
